import { ArrowDown, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScoreComparisonBarProps {
  /** Score before enrichment, relative to `maxScore`. */
  beforeScore: number;
  /** Score after enrichment, relative to `maxScore`. Delta is computed internally. */
  afterScore: number;
  /** Upper bound for beforeScore/afterScore (e.g. 10 or 100). Defaults to 10. */
  maxScore?: number;
  /** When true, hides the "Before" / "After" labels to the left of each bar. */
  hideLabels?: boolean;
  /** Raw product count behind `beforeScore`. Null/absent when the source never
   *  computed it — see {@link ScoreComparisonBar} for the fallback. */
  beforeCount?: number | null;
  /** Raw product count behind `afterScore`. Null/absent when uncomputed. */
  afterCount?: number | null;
  /** When false, the Before row is hidden entirely — used by a legend filter that
   *  solos the After series. Defaults to shown. */
  showBefore?: boolean;
  /** When false, the After row is hidden entirely — used by a legend filter that
   *  solos the Before series. Defaults to shown. */
  showAfter?: boolean;
  /** `data-testid` root; derives `-{before|after}-row` (+ `-bar`, `-value`) and `-delta-value`. */
  testId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp(value: number, maxScore: number): number {
  return Math.min(maxScore, Math.max(0, value));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Single horizontal score bar. Track is `surface-date-pill` (#E9ECF5);
 * fill is warning orange (before) or primary blue (after).
 * Height fixed at 7px per spec.
 */
function ScoreBar({
  score,
  maxScore,
  variant,
  testId,
}: {
  score: number;
  maxScore: number;
  variant: 'before' | 'after';
  /** `data-testid` for the progressbar. */
  testId: string;
}) {
  const pct = (clamp(score, maxScore) / maxScore) * 100;
  return (
    <div className="bg-surface-date-pill h-[7px] w-full overflow-hidden rounded-[8px]">
      <div
        className={cn('h-full rounded-[8px]', variant === 'before' ? 'bg-warning' : 'bg-primary')}
        style={{ width: `${String(pct)}%` }}
        role="progressbar"
        data-testid={testId}
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={maxScore}
      />
    </div>
  );
}

/**
 * Pill chip showing the delta with a directional arrow. `delta` drives the
 * sign and colour; `formatted` is the already-rendered magnitude, so scores
 * (one decimal) and counts (thousands-separated integer) share one chip.
 * Green (success-50 bg) for positive; brand-50 bg + destructive text for negative.
 */
function DeltaChip({
  delta,
  formatted,
  testId,
}: {
  delta: number;
  formatted: string;
  /** `data-testid` for the chip. */
  testId: string;
}) {
  const positive = delta > 0;
  const Icon = positive ? ArrowUp : ArrowDown;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md px-2 py-0.5',
        positive ? 'bg-success-50' : 'bg-brand-50',
      )}
      data-testid={testId}
    >
      <Icon
        className={cn('h-3 w-3 shrink-0', positive ? 'text-success' : 'text-destructive')}
        aria-hidden="true"
      />
      <span className={cn('text-xs font-semibold', positive ? 'text-success' : 'text-destructive')}>
        {formatted}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Compact before/after comparison with horizontal progress bars and a delta
 * chip on the After row. Four-column grid keeps label, bar, value, and chip
 * aligned across both rows so they never overlap.
 *
 * Bar length is always driven by the score. The *value* readout switches to
 * raw product counts ("1,842 products") when both counts are supplied, and
 * falls back to the score ("9.2/10") when either is null — counts are absent
 * on every run computed before they shipped, and a null count is "not
 * computed", never zero.
 */
export function ScoreComparisonBar({
  beforeScore,
  afterScore,
  maxScore = 10,
  hideLabels = false,
  beforeCount,
  afterCount,
  showBefore = true,
  showAfter = true,
  testId = 'score-comparison-bar',
}: ScoreComparisonBarProps) {
  const { t } = useTranslation('common');
  const showCounts = beforeCount != null && afterCount != null;

  // Chip tracks whichever readout is on screen, so the number under the bar
  // and the delta beside it always describe the same quantity.
  const delta = showCounts ? afterCount - beforeCount : afterScore - beforeScore;
  const deltaLabel = showCounts
    ? `${delta > 0 ? '+' : '-'}${Math.abs(delta).toLocaleString()}`
    : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`;

  /** Value readout under a bar — count when available, else score/maxScore. */
  const valueReadout = (
    score: number,
    count: number | null | undefined,
    tone: string,
    valueTestId: string,
  ) =>
    showCounts ? (
      <span className={`text-sm font-medium ${tone}`} data-testid={valueTestId}>
        {t('scoreComparisonBar.productsCount', {
          count: count ?? 0,
          formatted: (count ?? 0).toLocaleString(),
        })}
      </span>
    ) : (
      <>
        <span className={`text-sm font-medium ${tone}`} data-testid={valueTestId}>
          {score.toFixed(1)}
        </span>
        <span className="text-muted-foreground text-xs font-light">
          {t('scoreComparisonBar.outOf', { maxScore })}
        </span>
      </>
    );

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {/* Before row */}
      {showBefore && (
        <div
          className="grid grid-cols-[56px_1fr_auto_minmax(57px,auto)] items-center gap-x-3"
          data-testid={`${testId}-before-row`}
        >
          {hideLabels ? (
            <div aria-hidden="true" />
          ) : (
            <span className="text-foreground text-sm font-medium">Before</span>
          )}

          <ScoreBar
            score={beforeScore}
            maxScore={maxScore}
            variant="before"
            testId={`${testId}-before-bar`}
          />

          <div className="flex items-baseline gap-0.5">
            {valueReadout(beforeScore, beforeCount, 'text-warning', `${testId}-before-value`)}
          </div>

          {/* Spacer — keeps grid columns consistent with the After row */}
          <div aria-hidden="true" />
        </div>
      )}

      {/* After row */}
      {showAfter && (
        <div
          className="grid grid-cols-[56px_1fr_auto_minmax(57px,auto)] items-center gap-x-3"
          data-testid={`${testId}-after-row`}
        >
          {hideLabels ? (
            <div aria-hidden="true" />
          ) : (
            <span className="text-foreground text-sm font-medium">After</span>
          )}

          <ScoreBar
            score={afterScore}
            maxScore={maxScore}
            variant="after"
            testId={`${testId}-after-bar`}
          />

          <div className="flex items-baseline gap-0.5">
            {valueReadout(afterScore, afterCount, 'text-primary', `${testId}-after-value`)}
          </div>

          {/* Delta compares both series, so it only shows when both are visible. */}
          {showBefore && delta !== 0 ? (
            <DeltaChip delta={delta} formatted={deltaLabel} testId={`${testId}-delta-value`} />
          ) : (
            <div aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  );
}
